import type { RouteObject } from 'react-router';
import { AppLayout } from './components/AppLayout.js';
import { RequireAuth } from './components/RequireAuth.js';
import { AccountPage } from './pages/AccountPage.js';
import { AccountsPage } from './pages/AccountsPage.js';
import { AuditPage } from './pages/AuditPage.js';
import { AttachmentsPage } from './pages/AttachmentsPage.js';
import { BankPage } from './pages/BankPage.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { InboundEmailPage } from './pages/InboundEmailPage.js';
import { InvoicesPage } from './pages/InvoicesPage.js';
import { McpPage } from './pages/McpPage.js';
import { MOBILE_APP_PATH, MobileAppPage } from './pages/MobileAppPage.js';
import { MobileAppSettingsPage } from './pages/MobileAppSettingsPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { SaftPage } from './pages/SaftPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { TripsPage } from './pages/TripsPage.js';
import { VatPage } from './pages/VatPage.js';
import { JournalPage } from './pages/JournalPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { OauthConsentPage } from './pages/OauthConsentPage.js';

/**
 * The route tree.
 *
 * Paths are Danish, matching the navigation and the vocabulary the books are
 * kept in. They are user-visible text, and an English URL over a Danish screen
 * reads as a translation someone forgot to finish.
 *
 * All thirteen pages are built. `PlaceholderPage` carried the ones that were
 * routed before they existed — a dead link in the navigation looks like a bug,
 * and a blank page like a broken one — and it is deleted here rather than left
 * behind with no callers. It is in the history for the next feature that needs
 * routing before it is finished.
 */
export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  /*
    The consent screen for browser login (ADR-008).

    Signed-in, like everything below, but deliberately **outside AppLayout**: no
    sidebar, no company switcher. A second company picker beside the one this
    page is asking about is how a person grants access to the wrong books while
    reading the right name. The page itself carries the longer argument.

    A top-level route rather than a child of `ai-adgang`, which has no children:
    `/ai-adgang/godkend` would otherwise match nothing but the catch-all. The
    API builds this URL -- see PUBLIC_UI_BASE_URL in apps/api/src/config.ts --
    so the two have to agree, and there is a test on each side that they do.
  */
  {
    path: '/ai-adgang/godkend',
    element: (
      <RequireAuth>
        <OauthConsentPage />
      </RequireAuth>
    ),
  },
  /*
    The installed app (vite.config.ts: `start_url`).

    Signed-in, and **outside AppLayout**: two capture buttons and the company
    switcher, no sidebar. On a phone the full UI is a burger menu over thirteen
    desk pages, and what the phone is for is a receipt and a trip. The page
    carries the longer argument, including why `/app` is clear of Traefik's
    prefixes.
  */
  {
    path: MOBILE_APP_PATH,
    element: (
      <RequireAuth>
        <MobileAppPage />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'kladde', element: <JournalPage /> },
      { path: 'kontoplan', element: <AccountsPage /> },
      { path: 'bilag', element: <AttachmentsPage /> },
      { path: 'post', element: <InboundEmailPage /> },
      { path: 'bank', element: <BankPage /> },
      /*
        Danish path like every other route here. `koerebog` and not `kørebog`:
        a non-ASCII path is percent-encoded the moment it is shared, and a
        home-screen shortcut pointing at /k%C3%B8rebog is not a link anyone can
        read in a bug report.
      */
      { path: 'koerebog', element: <TripsPage /> },
      { path: 'fakturaer', element: <InvoicesPage /> },
      { path: 'kontakter', element: <ContactsPage /> },
      { path: 'moms', element: <VatPage /> },
      { path: 'rapporter', element: <ReportsPage /> },
      { path: 'saft', element: <SaftPage /> },
      { path: 'indstillinger', element: <SettingsPage /> },
      /*
        Company-scoped: this is one company's history and it changes completely
        when the switcher moves, so it sits in the sidebar under Indstillinger
        rather than in the header cluster. See AppLayout.
      */
      { path: 'revisionsspor', element: <AuditPage /> },
      /*
        Where the installed app is explained and installed, under Indstillinger.
        Not `/indstillinger/mobil-app`: the company details entry is active for
        any path starting `/indstillinger`, and would light up beside this one.
      */
      { path: 'mobil-app', element: <MobileAppSettingsPage /> },
      /*
        User-scoped pages. Reached from the header cluster, not the sidebar --
        neither changes when the company switcher moves. See AppLayout.
      */
      { path: 'min-konto', element: <AccountPage /> },
      /*
        `ai-adgang`, and NOT `/mcp` -- nor anything else starting with those
        three letters.

        In production Traefik owns that prefix: a router matches
        PathPrefix(`/mcp`) and hands it to the MCP server's container, ahead of
        the catch-all that serves this SPA. A page routed at `/mcp` would work
        perfectly in `vite dev` and return the MCP server's JSON-RPC refusal in
        production -- invisible until deployed, and then baffling.

        `/mcp-adgang` does not escape it either. Traefik's PathPrefix is a raw
        string prefix, not a segment match, so it captures `/mcp-anything`. The
        path has to begin with something else entirely.

        The nav label is still "MCP"; only the URL avoids the collision. See
        infrastructure/traefik/accountyob.yml.
      */
      { path: 'ai-adgang', element: <McpPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
